import { AiMappingController } from "../aiMappingControllerTypes";
import AiMappingMeasuredAreaTools from "./AiMappingMeasuredAreaTools";

export interface AiMappingToolbarMeasurements {
  selectedFeatureId: string | null;
  measurements: { featureId: string; measurementType: string }[];
}

interface AiMappingMeasuredAreaToolbarProps {
  aiMapping: AiMappingController;
  measurements: AiMappingToolbarMeasurements;
}

export default function AiMappingMeasuredAreaToolbar({
  aiMapping,
  measurements,
}: AiMappingMeasuredAreaToolbarProps) {
  const aiSegmentationCount = aiMapping.allResults.reduce(
    (count, result) =>
      count + result.segmentations.filter((segmentation) => !segmentation.isDeleted).length,
    0
  );
  const manualAreaCount = measurements.measurements.filter(
    (measurement) =>
      measurement.measurementType === "AREA" &&
      !aiMapping.aiMeasurementIds.includes(measurement.featureId)
  ).length;
  const manualSplitTargetSelected =
    !!measurements.selectedFeatureId &&
    !aiMapping.aiMeasurementIds.includes(measurements.selectedFeatureId) &&
    measurements.measurements.find(
      (measurement) => String(measurement.featureId) === String(measurements.selectedFeatureId)
    )?.measurementType === "AREA";

  const measuredAreaCount = aiSegmentationCount + manualAreaCount;
  const hasAiSelected = !!(aiMapping.selectedAreaId && aiMapping.selectedSegmentationId);
  const hasManualSelected = manualSplitTargetSelected;

  return (
    <AiMappingMeasuredAreaTools
      mergeDisabled={!aiMapping.mergeModeActive && measuredAreaCount === 0}
      mergeActive={aiMapping.mergeModeActive}
      mergeTitle={
        aiMapping.mergeModeActive
          ? "Draw a polygon around the areas to merge (click again to cancel)"
          : measuredAreaCount === 0
          ? "Measure or draw an area first"
          : "Click Merge, then draw a polygon touching the areas to merge"
      }
      splitDisabled={!aiMapping.splitModeActive && measuredAreaCount === 0}
      splitActive={aiMapping.splitModeActive}
      splitTitle={
        aiMapping.splitModeActive
          ? "Draw a line across the area to split it"
          : hasAiSelected || hasManualSelected
          ? "Draw a line across the selected area to split it"
          : "Click Split, then click an area and draw the cut line"
      }
      removeDisabled={!aiMapping.eraseModeActive && measuredAreaCount === 0}
      removeTitle={
        aiMapping.eraseModeActive
          ? "Draw a polygon over the areas to erase them (click again to cancel)"
          : "Click to draw a polygon over the areas to erase"
      }
      removeActive={aiMapping.eraseModeActive}
      onMerge={() => {
        if (aiMapping.checkedSegmentationIds.length >= 2 && aiMapping.selectedAreaId) {
          aiMapping.mergeSegmentations(aiMapping.selectedAreaId, aiMapping.checkedSegmentationIds);
          return;
        }
        aiMapping.toggleMergeMode();
      }}
      onSplit={() => {
        if (hasAiSelected) {
          aiMapping.splitSegmentation(aiMapping.selectedAreaId!, aiMapping.selectedSegmentationId!);
          return;
        }
        aiMapping.toggleSplitMode();
      }}
      onRemove={() => {
        aiMapping.toggleEraseMode();
      }}
    />
  );
}
